import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	PageHeader,
	Table,
	Card,
	Icon,
	Modal,
	Typography,
	Input,
	message,
	Button,
	Dropdown,
	Menu,
	Row,
	Col,
} from 'antd';

import Meta from '../../../../components/Meta';
import Form from '../../../../components/Form';
import Fallback from '../../../../components/Fallback';
import { Container } from './styles';

import AiAgentAccentsApi from '../../../../services/sdks/ai-agent-accents';

const { Search } = Input;

const breadcrumb = {
	routes: [
		{ breadcrumbName: 'Início' },
		{ breadcrumbName: 'Agentes de IA' },
		{ breadcrumbName: 'Sotaques' },
	],
	style: { marginBottom: 12 },
};

const AiAgentAccents = () => {
	const [fallback, setFallback] = useState({ initialData: true });
	const [accents, setAccents] = useState([]);
	const [showUpsertModal, setShowUpsertModal] = useState(false);
	const [accent, setAccent] = useState(null);
	const [loading, setLoading] = useState(false);

	const [pagination, setPagination] = useState({
		current: 1,
		perPage: 10,
		total: 0,
		pages: 0,
	});

	const [filters, setFilters] = useState({
		title: '',
	});

	const isFormValid = useMemo(() => {
		if (!accent?.title || !accent?.text) {
			return false;
		}
		return true;
	}, [accent]);

	const columns = [
		{
			title: 'Título',
			key: 'title',
			dataIndex: 'title',
		},
		{
			title: 'Sotaque',
			key: 'text',
			dataIndex: 'text',
			render: (text) => (text.length <= 50 ? text : `${text.slice(0, 50)}...`),
		},
		{
			title: 'Ações',
			key: 'actions',
			align: 'right',
			render: (accent) => (
				<Dropdown
					placement='bottomLeft'
					overlay={
						<Menu>
							<Menu.Item
								key='details'
								onClick={() => {
									setAccent(accent);
									setShowUpsertModal(true);
								}}>
								<Icon type='edit' /> Editar
							</Menu.Item>

							<Menu.Divider />

							<Menu.Item
								key='delete'
								className='ant-dropdown-menu-item-danger'
								onClick={() => {
									Modal.confirm({
										title: 'Excluir Sotaque?',
										icon: 'exclamation-circle',
										content:
											'Essa ação não poderá ser revertida, realmente deseja excluir esse Sotaque?',
										okText: 'Excluir',
										onOk: () => handleDeleteAccent(accent?._id),
										okButtonProps: {
											icon: 'delete',
											type: 'danger',
										},
										cancelText: 'Cancelar',
										cancelButtonProps: {
											icon: 'close-circle',
										},
									});
								}}>
								<Icon type='delete' /> Excluir
							</Menu.Item>
						</Menu>
					}>
					<Icon style={{ cursor: 'pointer', fontSize: 20, marginRight: 12 }} type='more' />
				</Dropdown>
			),
		},
	];

	const fetchInitialData = useCallback(
		async (page = 1, perPage = pagination.perPage, searchFilters = {}) => {
			try {
				setLoading(true);

				const params = { page, perPage };

				if (searchFilters.title) {
					params.title = searchFilters.title;
				}

				const response = await AiAgentAccentsApi.getAllAiAccents(params);
				const { accents, pagination: apiPagination } = response.data;

				setAccents(accents);

				setPagination({
					current: page,
					perPage: perPage,
					total: apiPagination?.total || 0,
					pages: apiPagination?.pages || 0,
				});
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar os Sotaques, tente novamente');
				setAccents([]);
				setPagination({
					current: 1,
					perPage: pagination.perPage,
					total: 0,
					pages: 0,
				});
			} finally {
				setLoading(false);
				setFallback((prev) => ({ ...prev, initialData: false }));
			}
		},
		[pagination.perPage]
	);

	const handleDeleteAccent = useCallback(
		async (accentId) => {
			try {
				await AiAgentAccentsApi.deleteAiAgentAccent(accentId);

				fetchInitialData(pagination.current, pagination.perPage);

				return message.success('Sotaque excluído com sucesso');
			} catch (error) {
				console.error(error);
				message.error('Houve um erro, tente novamente');
			}
		},
		[fetchInitialData, pagination]
	);

	const handleUpsertAccent = useCallback(async () => {
		setFallback((prev) => ({ ...prev, upsert: true }));

		try {
			const isUpdate = !!accent?._id;
			const payload = {
				title: accent.title,
				text: accent.text,
			};

			if (isUpdate) {
				await AiAgentAccentsApi.updateAiAgentAccent(accent?._id, payload);
				fetchInitialData(pagination.current, pagination.perPage, filters);
			} else {
				await AiAgentAccentsApi.createAiAgentAccent(payload);
				fetchInitialData(1, pagination.perPage, filters);
			}

			setAccent(null);
			setShowUpsertModal(false);
			message.success('Sotaque criada/atualizada com sucesso');
		} catch (error) {
			message.error('Erro ao criar/atualizar Sotaque');
		}

		setFallback((prev) => ({ ...prev, upsert: false }));
	}, [fetchInitialData, filters, pagination, accent?._id, accent?.text, accent?.title]);

	useEffect(() => {
		setPagination((prev) => ({ ...prev, perPage: 10 }));
		fetchInitialData(1, 10);
	}, [fetchInitialData]);

	const handlePageChange = useCallback(
		(page, perPage) => {
			fetchInitialData(page, perPage, filters);
		},
		[fetchInitialData, filters]
	);

	const handleSearch = useCallback(
		(value) => {
			const newFilters = { ...filters, title: value };
			setFilters(newFilters);
			fetchInitialData(1, pagination.perPage, newFilters);
		},
		[filters, pagination.perPage, fetchInitialData]
	);

	const handleClearFilters = useCallback(() => {
		const clearedFilters = { title: '' };
		setFilters(clearedFilters);
		fetchInitialData(1, pagination.perPage, clearedFilters);
	}, [pagination.perPage, fetchInitialData]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor aguarde...' />;
	}

	return (
		<>
			<Meta title='Sotaques dos Agentes' />

			<PageHeader
				title='Sotaques dos Agentes'
				breadcrumb={breadcrumb}
				extra={[
					<Button
						key='create-banner'
						type='ghost'
						size='large'
						icon='plus-circle'
						onClick={() => {
							setShowUpsertModal(true);
							setAccent({});
						}}>
						Novo Sotaque
					</Button>,
				]}>
				<Typography.Text>Sotaques dos Agentes de IA adicionados pelo ADMIN</Typography.Text>
			</PageHeader>

			<Container>
				<Card style={{ marginBottom: 16 }}>
					<Row gutter={20} align='middle'>
						<Col xs={24} sm={8} md={4}>
							<Search
								placeholder='Buscar por título'
								allowClear
								onSearch={handleSearch}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} sm={8} md={4}>
							<Button icon='reload' onClick={handleClearFilters} style={{ width: '100%' }}>
								Limpar Filtros
							</Button>
						</Col>
					</Row>
				</Card>

				<Card>
					<Table
						size='middle'
						rowKey='_id'
						columns={columns}
						dataSource={accents}
						loading={loading}
						style={{ border: 'none' }}
						pagination={{
							size: 'large',
							pageSize: pagination.perPage,
							hideOnSinglePage: true,
							current: pagination.current,
							total: pagination.total,
							onChange: handlePageChange,
							onShowSizeChange: handlePageChange,
							showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
						}}
					/>
				</Card>
			</Container>

			<Modal
				title={
					<>
						<Icon type='file-text' style={{ marginRight: 8 }} /> {accent?._id ? 'Editar' : 'Criar'}{' '}
						Sotaque do Agente
					</>
				}
				width={600}
				visible={showUpsertModal}
				okButtonProps={{ disabled: !isFormValid, loading: fallback?.upsert }}
				okText={`${accent?._id ? 'Editar' : 'Criar'} Sotaque`}
				onOk={handleUpsertAccent}
				onCancel={() => {
					setShowUpsertModal(false);
					setAccent(null);
				}}>
				<Form.Container layout='1fr'>
					<Form.Item label='Título'>
						<Input
							placeholder='Informe o título do Sotaque'
							value={accent?.title}
							onChange={(e) => setAccent({ ...accent, title: e.target.value })}
						/>
					</Form.Item>
					<Form.Item label='Regra do Sotaque'>
						<Input.TextArea
							placeholder='Informe a regra do Sotaque'
							value={accent?.text}
							onChange={(e) => setAccent({ ...accent, text: e.target.value })}
							rows={18}
						/>
					</Form.Item>
				</Form.Container>
			</Modal>
		</>
	);
};

export default AiAgentAccents;
